import React, { useEffect } from 'react';

import { FormikProps } from 'formik';

import {
  ICalendarEventType,
  RecurrenceActions,
  ServiceTypes,
  calendarDate,
  deleteWorkshift,
  getPharmacyCalendar,
  getPharmacyWorkshift,
  getRecurrence,
  pharmacyActions,
  pharmacyWorkshift,
  reset,
  resetStatus,
  setBookedContractDates,
  setRecurrence,
  successOrSelector,
  successSelector,
} from '@pharmaplan/common';
import ServiceHandler from '@pharmaplan/common/helpers/ServiceHandler';

import { selectFooterPaginationCurrent } from '../../selectors/drawerSelector';
import {
  renderScreen,
  resetDrawer,
  setBack,
  setButtons,
  setFooterShow,
  setPagination,
} from '../../actions/drawerActions';
import strings from '../../localization';
import { setRecurrenceProps } from '../../helpers/workshift/functions';

import { useAppSelector } from '../useAppSelector';
import { useAppDispatch } from '../useAppDispatch';
import useSelfService from '../useSelfService';
import { ScreenTypes } from '../../helpers/Constants';
import { setDialog } from '../../reducers/dialogReducer';
import MultiShiftDelete from '../../components/MultiShiftDelete';

const { series } = RecurrenceActions;

const useUpdateWorkshift = (
  eventIds: Array<string>,
  formik: FormikProps<any>,
  recurrenceId?: string,
  recType?: RecurrenceActions,
  type?: ICalendarEventType,
) => {
  const dispatch = useAppDispatch();
  const { isSelfService } = useSelfService();

  const current = useAppSelector(selectFooterPaginationCurrent);
  const currentWorkshift = useAppSelector(pharmacyWorkshift);
  const recurrence = useAppSelector(getRecurrence);
  const cDate = useAppSelector(calendarDate);

  const serviceType = ServiceHandler.getService();

  const updateSuccess = useAppSelector((state) =>
    successOrSelector(
      [
        pharmacyActions.updateWorkshift,
        pharmacyActions.updateWorkshiftContract,
      ],
      state,
    ));
  const deleteSuccess = useAppSelector((state) =>
    successSelector([pharmacyActions.deleteWorkshift], state));

  const id = eventIds[current - 1];
  const isSeries = recType === series;
  const { handleSubmit } = formik ?? {};

  const { recurrenceId: currentRecurrenceId, endDate, longTermShiftContract } = currentWorkshift ?? {};
  const { longTermShiftContractId } = longTermShiftContract ?? {};

  const handleReset = () => {
    dispatch(reset());
    dispatch(resetDrawer());
  };

  const confirmMultipleDelete = () => {
    dispatch(
      setDialog({
        heading: {
          title: strings.deleteShifts,
        },
        Component: (
          <MultiShiftDelete
            longTermShiftContractId={longTermShiftContractId ?? ''}
          />
        ),
        maxWidth: 'lg',
        showCloseButton: true,
      }),
    );
  };

  const singleDelete = () => {
    dispatch(
      deleteWorkshift({
        id,
        recurrenceId: isSeries ? recurrenceId : '',
      }),
    );
  };

  const serviceConfig = {
    [ServiceTypes.full]: {
      secondaryLabel: strings.deleteWorkshift,
      secondaryAction: longTermShiftContractId ? confirmMultipleDelete : singleDelete,
    },
    [ServiceTypes.self]: {
      secondaryLabel: strings.discardChanges,
      secondaryAction: handleReset,
    },
  };

  const { secondaryAction, secondaryLabel } = serviceConfig[serviceType];

  const handlePagination = () => {
    if (isSelfService) {
      dispatch(
        setPagination({ show: false, current: 1, total: eventIds.length }),
      );
      return;
    }

    dispatch(
      setPagination({
        show: true,
        total: eventIds.length,
        current: 1,
      }),
    );
  };

  const goBack = () => {
    dispatch(
      renderScreen({
        screenNumber: 5,
        screenType: ScreenTypes.workshiftSummary,
        date: currentWorkshift.startDate,
        type,
      }),
    );
  };

  useEffect(() => {
    handlePagination();
    dispatch(setFooterShow(true));
    dispatch(
      setButtons({
        show: true,
        primaryButton: {
          label: strings.saveChanges,
          onClick: handleSubmit,
        },
        secondaryButton: {
          label: secondaryLabel,
          variant: 'text',
          onClick: secondaryAction,
        },
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [secondaryAction]);

  useEffect(() => {
    dispatch(setBack(goBack));
    dispatch(
      setBookedContractDates(
        currentWorkshift.bookedDates?.map?.(
          (item) =>
            item?.split?.('T')?.[0],
        ) ?? [],
      ),
    );
    currentRecurrenceId && isSeries
      ? setRecurrenceProps(dispatch, recurrence, currentWorkshift)
      : dispatch(
        setRecurrence({
          ...recurrence,
          endDate,
          repeatEvery: 0,
        }),
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentWorkshift]);

  useEffect(() => {
    if (updateSuccess || deleteSuccess) {
      dispatch(getPharmacyCalendar(cDate));
      handleReset();
      dispatch(resetStatus());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateSuccess, deleteSuccess]);

  useEffect(() => {
    dispatch(getPharmacyWorkshift(id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
};

export default useUpdateWorkshift;
